<template>
  <nav
    class="nav"
    data-state="expanded"
    aria-label="Primary">
    <header class="nav-brand">
      <span class="nav-brand-name" data-testid="nav-brand-name">{{ labName }}</span>
    </header>
    <div class="px-[var(--space-stack-sm)] pt-[var(--space-stack-sm)]">
      <UiBaseButton
        isFullWidth
        :text="createButtonLabel"
        type="button"
        @click="onCreateClick" />
    </div>
    <ul class="nav-list">
      <li v-for="item in primaryItems" :key="item.name">
        <router-link
          :to="{ name: item.name }"
          class="nav-item"
          :aria-current="isActive(item.matchPrefix) ? 'page' : undefined"
          :title="item.label">
          <span class="nav-item-icon" aria-hidden="true">
            <component :is="item.icon" />
          </span>
          <span class="nav-item-label">{{ item.label }}</span>
        </router-link>
      </li>
    </ul>
  </nav>

  <div
    v-if="isBottomBarVisible"
    class="nav-bottom-wrap nav-bottom-wrap--primary"
    :class="{ 'is-hidden': isBottomNavHidden }">
    <div v-if="showListActions" class="flex items-center gap-[var(--space-stack-sm)]">
      <div class="nav-pill nav-search-pill">
        <IconLucideSearch class="nav-search-icon" aria-hidden="true" />
        <input
          :value="currentSearchQuery"
          type="search"
          class="nav-search-input"
          :placeholder="$t('W: Søg')"
          :aria-label="$t('W: Søg')"
          @input="onSearchInput">
        <!-- Our own clear button, shown whenever there is text — the browser's native one (hidden in
             base.css) only appears while the field has focus, and the query survives leaving the
             list, so it has to be clearable from outside the field too. -->
        <button
          v-if="currentSearchQuery"
          type="button"
          class="flex h-5 w-5 flex-none cursor-pointer items-center justify-center text-text-secondary"
          :aria-label="$t('W: Ryd søgning')"
          :title="$t('W: Ryd søgning')"
          @click="onClearSearchClick">
          <IconLucideX class="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      <button
        ref="createCircleRef"
        type="button"
        class="nav-circle nav-circle--create"
        :aria-label="createButtonLabel"
        :title="createButtonLabel"
        @pointerdown="onCreatePress"
        @pointerup="onCreateRelease"
        @pointerleave="onCreateRelease"
        @click="onCreateClick">
        <IconLucidePlus class="nav-icon-action" aria-hidden="true" />
      </button>
    </div>

    <CreateChooserSheet
      :isOpen="isCreateChooserOpen"
      @on-cancel="isCreateChooserOpen = false"
      @on-select-order="onSelectCreateOrder"
      @on-select-project="onSelectCreateProject" />

    <nav v-if="isBottomTabsVisible" class="nav-bottom" aria-label="Primary">
      <router-link
        v-for="tab in bottomTabs"
        :key="tab.name"
        :to="{ name: tab.name }"
        class="nav-bottom-item"
        :aria-current="isActive(tab.matchPrefix) ? 'page' : undefined">
        <span class="nav-bottom-item-content">
          <span class="nav-bottom-item-icon" aria-hidden="true">
            <component :is="tab.icon" />
          </span>
          <span>{{ tab.label }}</span>
        </span>
      </router-link>
    </nav>
  </div>
</template>

<script lang="ts">
import IconLucideFolder from '~icons/lucide/folder'
import IconLucideBeaker from '~icons/lucide/beaker'
import IconLucideUser from '~icons/lucide/user'
import { mapActions, mapState } from 'pinia'
import { useAccountStore } from '@/stores/accountStore'
import { useOrderStore } from '@/stores/orderStore'
import { useProjectStore } from '@/stores/projectStore'
import { pressNavElementDown, pressNavElementUp } from '@/globals/typescript/utils/navAnimations'
import type { Component } from 'vue'

interface INavItem {
  name: string
  label: string
  icon: Component
  matchPrefix: string
}

export default {
  name: 'CustomerNav',
  data() {
    return {
      isCreateChooserOpen: false,
    }
  },
  computed: {
    ...mapState(useAccountStore, ['isProjectsEnabled', 'labAccount']),
    // The lab the signed-in user belongs to (e.g. "EFI Global"); "Milva" only until it has loaded.
    labName(): string {
      return this.labAccount?.name || 'Milva'
    },
    // Each list surface owns its own searchQuery field (orderStore/
    // projectStore) that its own list view filters off — see
    // OrdersListView.vue/ProjectsListView.vue's matching comments. This one
    // persistent search input in the nav reads/writes whichever store
    // matches the current route, via currentSearchQuery/onSearchInput below.
    ...mapState(useOrderStore, { orderSearchQuery: 'searchQuery' }),
    ...mapState(useProjectStore, { projectSearchQuery: 'searchQuery' }),
    // The desktop sidebar lists Projects (hidden, not disabled, while
    // isProjectsEnabled is false), Orders and Account. The mobile bottom bar
    // mirrors it except for Account: on mobile the header initials already
    // link to the Account ("Mig") page, so it is not repeated as a tab. (An
    // earlier version of bottomTabs hardcoded Orders/Account only,
    // unconditionally — found live 2026-09-29: an account with Projects
    // enabled had no way to reach the Projekter list from the mobile nav at
    // all. Fixed to mirror primaryItems.)
    primaryItems(): INavItem[] {
      const items: INavItem[] = []
      if (this.isProjectsEnabled) {
        items.push({ name: 'Projects', label: this.$t('W: Projekter'), icon: IconLucideFolder, matchPrefix: '/app/projects' })
      }
      items.push(
        { name: 'Orders', label: this.$t('W: Ordrer'), icon: IconLucideBeaker, matchPrefix: '/app/orders' },
        { name: 'Account', label: this.$t('W: Mig'), icon: IconLucideUser, matchPrefix: '/app/account' }
      )
      return items
    },
    bottomTabs(): INavItem[] {
      const tabs: INavItem[] = []
      if (this.isProjectsEnabled) {
        tabs.push({ name: 'Projects', label: this.$t('W: Projekter'), icon: IconLucideFolder, matchPrefix: '/app/projects' })
      }
      tabs.push({ name: 'Orders', label: this.$t('W: Ordrer'), icon: IconLucideBeaker, matchPrefix: '/app/orders' })
      return tabs
    },
    // A lone tab (Orders, while Projects is off) is not a choice, so the tab
    // row only renders once there is something to switch between.
    isBottomTabsVisible(): boolean {
      return this.bottomTabs.length > 1
    },
    // The bar is the panel holding the search+create row and the tab row;
    // with neither of them to show (e.g. the Account pages while Projects is
    // off) it must not render at all, or it would be an empty bordered strip.
    isBottomBarVisible(): boolean {
      return this.showListActions || this.isBottomTabsVisible
    },
    isBottomNavHidden(): boolean {
      return !!this.$route.meta.hideBottomNav
    },
    // Persistent search+create row shows on both list surfaces (Orders,
    // Projects) — not on Account/Profile, and not on a single order's own
    // detail page (MIL-338) or a project's own detail page, where there's no
    // list to search or create into.
    showListActions(): boolean {
      return this.$route.name === 'Orders' || this.$route.name === 'Projects'
    },
    currentSearchQuery(): string {
      return this.$route.name === 'Projects' ? this.projectSearchQuery : this.orderSearchQuery
    },
    createButtonLabel(): string {
      return this.$t('W: Opret ordre')
    },
  },
  methods: {
    ...mapActions(useOrderStore, { setOrderSearchQuery: 'setSearchQuery' }),
    ...mapActions(useProjectStore, { setProjectSearchQuery: 'setSearchQuery' }),
    isActive(matchPrefix: string): boolean {
      return this.$route.path.startsWith(matchPrefix)
    },
    setCurrentSearchQuery(value: string): void {
      if (this.$route.name === 'Projects') {
        this.setProjectSearchQuery(value)
      } else {
        this.setOrderSearchQuery(value)
      }
    },
    onSearchInput(event: Event): void {
      this.setCurrentSearchQuery((event.target as HTMLInputElement).value)
    },
    onClearSearchClick(): void {
      this.setCurrentSearchQuery('')
    },
    // MIL-334 Phase 3 — opens the real "create order without a project"
    // page. Independent of `isProjectsEnabled`: a project-less order works
    // regardless of whether Projects itself ever ships (see the "Lab
    // Customer — Create Order & Samples" plan's Decisions Made).
    //
    // "Lab Customer — Projects, Floor Plans & Samples" plan, Phase 6 — once
    // isProjectsEnabled is true, the "+" button offers a choice ("Ny ordre" /
    // "Nyt projekt") instead of going straight to order creation. This must
    // not regress the project-less flow above when the flag is off.
    onCreateClick(): void {
      if (this.isProjectsEnabled) {
        this.isCreateChooserOpen = true
        return
      }
      this.$router.push({ name: 'CreateOrder' })
    },
    onSelectCreateOrder(): void {
      this.isCreateChooserOpen = false
      this.$router.push({ name: 'CreateOrder' })
    },
    onSelectCreateProject(): void {
      this.isCreateChooserOpen = false
      this.$router.push({ name: 'ProjectNew' })
    },
    // Tap feedback (scale 1→0.95, ease-out) for the floating create circle —
    // pointerdown/up rather than a CSS :active state, so it also releases on
    // pointerleave (drag-off-and-lift) not just a clean tap.
    onCreatePress(): void {
      pressNavElementDown(this.$refs.createCircleRef as HTMLElement | undefined)
    },
    onCreateRelease(): void {
      pressNavElementUp(this.$refs.createCircleRef as HTMLElement | undefined)
    },
  },
}
</script>
